/* eslint-disable @typescript-eslint/member-ordering */
import { trigger, state, style, transition, animate } from '@angular/animations';
import { HttpStatusCode } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, output, signal, untracked } from '@angular/core';
import { faMessageExclamation } from '@fortawesome/pro-solid-svg-icons';

import { DEFAULT_LANGUAGE, TranslateLoader, TranslateService, TranslateStore } from '@ngx-translate/core';
import { plainToClass } from 'class-transformer';
import { EMPTY, catchError, Observable, take, tap } from 'rxjs';

import { NotificationLevel, NotificationsService } from '@eiq/notifications';

import { ProjectAnswerStatus } from '@empathy/global/data';
import { AiChatPageState, SurveyFeedback, SurveyResult, SurveySettings } from '@empathy/survey/base';
import { createSystemTranslateLoader, PageModel, TranslationsService } from '@empathy/survey-core';

import { StartSurvey } from '../../models/start-survey.interface';
import { SurveyCompleteResponse } from '../../models/survey-complete-response.interface';
import { SurveyNavigationContract } from '../../models/survey-navigation-contract.interface';
import type { SurveyNavigationErrorAction } from '../../models/survey-navigation-error-action.type';
import { SURVEY_NAVIGATION_ERROR_MESSAGES } from '../../models/survey-navigation-error-messages.constant';
import { ISurveyNavigationResponse } from '../../models/survey-navigation-response.interface';
import { SurveyAnswerParserService } from '../../services/survey-answer-parser.service';
import { SurveyNavigationService } from '../../services/survey-navigation.service';
import { SurveyViewerStateService } from '../../services/survey-viewer-state.service';
import { SurveyViewerService } from '../../services/survey-viewer.service';

@Component({
  selector: 'empathy-viewer',
  templateUrl: 'viewer.component.html',
  styleUrls: ['./viewer.component.scss', './_viewer-theme.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger('openingAnimation', [state('void', style({ opacity: 0 })), transition(':enter', [animate('300ms ease-in')])])],
  providers: [
    TranslateStore,
    { provide: DEFAULT_LANGUAGE, useValue: null },
    TranslateService,
    {
      provide: TranslateLoader,
      useFactory: createSystemTranslateLoader,
      deps: [TranslationsService]
    }
  ]
})
export class ViewerComponent implements OnDestroy {
  private surveyViewerService = inject(SurveyViewerService);

  startSurvey = input<StartSurvey>();
  settings = input<SurveySettings>();
  surveyTitle = input<string>();
  previewMode = input(false);

  completed = output<SurveyCompleteResponse>();
  forceComplete = output<{ statusId: number; redirectUrl: string }>();
  started = output();

  activePage$ = signal<PageModel>(null);
  activePageChatState$ = signal<AiChatPageState>({});
  readonly isPreparing$ = computed(() => this.startSurvey() === null);
  readonly isTestMode$ = this.surveyViewerService.isTestMode$;
  navigationErrorAction$ = signal<SurveyNavigationErrorAction | null>(null);
  completionPending$ = signal(false);
  resumeAnswers$ = signal<SurveyResult>(null);
  showEndPage$ = signal(false);
  showFeedbackPage$ = signal(false);
  readonly welcomeDetails$ = computed(() => {
    if (this.activePage$() || !this.startSurvey().welcome) {
      return null;
    }

    return {
      ...this.startSurvey().welcome,
      title: this.surveyTitle(),
      useTitleInd: this.settings()?.useTitleInd
    };
  });

  readonly faMessageExclamation = faMessageExclamation;

  private surveyAnswerParserService = inject(SurveyAnswerParserService);
  private surveyViewerStateService = inject(SurveyViewerStateService);
  private translate = inject(TranslateService);
  private surveyNavigationService: SurveyNavigationContract = inject(SurveyNavigationService);
  private notificationsService = inject(NotificationsService);

  private navigationErrorNotificationRef: { dismiss: () => void } = null;
  private surveyCompleteResponse: SurveyCompleteResponse;

  constructor() {
    effect(
      () => {
        if (this.startSurvey()) {
          if (this.startSurvey().languageCode) {
            this.translate.use(this.startSurvey().languageCode);
          }
          this.surveyViewerService.answerId$.set(this.startSurvey().answerId);
          this.surveyViewerService.version$.set(this.startSurvey().version ?? 0);
          this.surveyViewerService.isBackButtonDisabled$.set(this.settings().isBackButtonDisabled);
          this.surveyViewerService.canGoBackInd$.set(this.startSurvey().canGoBackInd);
          this.surveyViewerService.progress$.set(this.startSurvey().progress);
          if (!this.startSurvey().welcome) {
            untracked(() => this.setActivePage(this.startSurvey()));
          }
        }
      },
      { allowSignalWrites: true }
    );
    effect(
      () => {
        const page = this.activePage$();
        if (page) {
          const automaticIds = this.surveyViewerService.automaticQuestionIds$();
          untracked(() =>
            this.surveyViewerStateService.setActivePage(
              plainToClass(PageModel, {
                ...page,
                questions: page.questions.filter((question) => !automaticIds.has(question.id))
              })
            )
          );
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnDestroy(): void {
    this.dismissNavigationErrorNotification();
    this.surveyViewerService.destroy();
  }

  onStart(): void {
    this.started.emit();
    this.setActivePage(this.startSurvey());
  }

  onPrev(): void {
    if (this.surveyNavigationService.isGoingBack$()) {
      return;
    }

    const currentPageId = this.activePage$()?.id;
    if (!currentPageId) {
      // Back requires the active page as its authoritative cursor. If no page is
      // rendered yet, there is no navigation request or loading/error state to show.
      return;
    }

    this.dismissNavigationErrorNotification();
    this.surveyNavigationService
      .goBack(currentPageId)
      .pipe(catchError((err) => this.processNavigationError(err, 'back')))
      .subscribe((res) => this.processNavigationResponse(res));
  }

  onNext(): void {
    if (this.completionPending$()) {
      this.completeSurvey();
      return;
    }

    this.dismissNavigationErrorNotification();
    this.surveyNavigationService
      .goNext()
      .pipe(catchError((err) => this.processNavigationError(err, 'next')))
      .subscribe((res) => this.processNavigationResponse(res));
  }

  onHandleCommunicationError(): void {
    this.forceComplete.emit({ statusId: ProjectAnswerStatus.Expired, redirectUrl: null });
  }

  onComplete(): void {
    this.completed.emit(this.surveyCompleteResponse);
  }

  onSubmitFeedback(feedback: SurveyFeedback): void {
    this.surveyNavigationService.sendFeedback(this.surveyViewerService.inviteId$(), feedback).subscribe();

    if (!!this.startSurvey().endPage) {
      this.showEndPage$.set(!!this.startSurvey().endPage);
      this.showFeedbackPage$.set(false);
    } else {
      this.onComplete();
    }
  }

  private setActivePage(activeState: {
    chatState?: AiChatPageState;
    currentPage: PageModel;
    existingAnswerJson: string;
    completionPendingInd?: boolean;
  }): void {
    if (!activeState.currentPage) {
      if (activeState.completionPendingInd) {
        this.completeSurvey();
      }
      return;
    }
    this.activePage$.set(plainToClass(PageModel, activeState.currentPage));
    this.activePageChatState$.set(activeState.chatState ?? {});
    this.resumeAnswers$.set(plainToClass(SurveyResult, this.surveyAnswerParserService.parseExistingAnswers(activeState.existingAnswerJson)));
  }

  private processNavigationResponse(res: ISurveyNavigationResponse): void {
    this.surveyViewerService.answerId$.set(res.answerId);
    this.surveyViewerService.version$.set(res.version ?? 0);
    if (res.progress != null) {
      this.surveyViewerService.progress$.set(res.progress);
    }

    // Assigned unconditionally: a falsy guard here would never let the capability go back to false,
    // which is exactly what hides the Back button once there is no earlier page to return to.
    this.surveyViewerService.canGoBackInd$.set(res.canGoBackInd);

    if (res.currentPage) {
      this.completionPending$.set(false);
      this.setActivePage(res);
      return;
    }

    if (res.statusId === ProjectAnswerStatus.Incomplete) {
      this.completionPending$.set(true);
      this.completeSurvey();
      return;
    }

    this.completionPending$.set(false);
    this.forceComplete.emit({ statusId: res.statusId, redirectUrl: res.redirectUrl });
  }

  private completeSurvey(): void {
    this.dismissNavigationErrorNotification();
    this.completionPending$.set(true);
    this.surveyNavigationService
      .complete()
      .pipe(
        tap((res) => (this.surveyCompleteResponse = res)),
        catchError((err) => this.processNavigationError(err, 'complete'))
      )
      .subscribe(() => {
        this.completionPending$.set(false);
        this.endSurvey();
      });
  }

  private endSurvey(): void {
    if (this.settings().useFeedbackInd) {
      this.showFeedbackPage$.set(true);
      return;
    } else if (!!this.startSurvey().endPage) {
      this.showEndPage$.set(true);
      return;
    }

    this.onComplete();
  }

  private processNavigationError(err: { status: HttpStatusCode }, action: SurveyNavigationErrorAction): Observable<never> {
    if (err.status === HttpStatusCode.Unauthorized) {
      this.forceComplete.emit({ statusId: ProjectAnswerStatus.Expired, redirectUrl: null });
      return EMPTY;
    }

    this.navigationErrorAction$.set(action);
    this.showNavigationErrorNotification(action);
    return EMPTY;
  }

  private dismissNavigationErrorNotification(): void {
    this.navigationErrorAction$.set(null);
    this.navigationErrorNotificationRef?.dismiss();
    this.navigationErrorNotificationRef = null;
  }

  private showNavigationErrorNotification(action: SurveyNavigationErrorAction): void {
    const titleKey = `system.navigationError.${action}Title`;
    const messageKey = `system.navigationError.${action}Message`;

    this.translate
      .get([titleKey, messageKey])
      .pipe(take(1))
      .subscribe((translations) => {
        if (this.navigationErrorAction$() !== action) {
          return;
        }

        this.navigationErrorNotificationRef?.dismiss();
        const fallback = SURVEY_NAVIGATION_ERROR_MESSAGES[action];
        this.navigationErrorNotificationRef = this.notificationsService.open({
          duration: null,
          level: NotificationLevel.Error,
          message: translations[messageKey] && translations[messageKey] !== messageKey ? translations[messageKey] : fallback.message,
          title: translations[titleKey] && translations[titleKey] !== titleKey ? translations[titleKey] : fallback.title
        });
      });
  }
}
